import { AxiosRequestConfig } from 'axios'
import dayjs from 'dayjs'
import epsClient from 'enrollment-platform/api/eps.client'
import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import { BenchmarkSnapshot } from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/Contribution/types/types'
import { normalizeMembersSearchResponse } from 'routes/Employees/PersonSearch/store/mappers/membersSearch.mapper'
import { DemoCustomerStep1Data } from '../demoCustomer.schema'
import {
  CreateDemoCustomerResponse,
  DemoContributionStatusResponse,
  DemoPopulationResult,
  DemoSetupResult,
  SalesContactFields,
} from '../types'

/** Base path for demo-specific endpoints on a given customer. */
const demoBase = (customerId: string) => `/api/v1/customers/${customerId}/demo`

/**
 * Serialises form values into a JSON body for POST /api/v1/customers/demo.
 */
function serializeCreateRequest(data: DemoCustomerStep1Data) {
  const contact: SalesContactFields | undefined = data.salesContact
    ? (() => {
        const c: SalesContactFields = {
          fullName: data.salesContact?.fullName,
          title: data.salesContact?.title,
          phone: data.salesContact?.phone || undefined,
          email: data.salesContact?.email,
        }
        return Object.values(c).some((v) => v !== undefined && v !== '')
          ? c
          : undefined
      })()
    : undefined

  return {
    companyName: data.companyName,
    payrollCycle: data.payrollCycle,
    benefitEffectiveDate: dayjs(data.benefitEffectiveDate).format('YYYY-MM-DD'),
    enrollmentStartDate: dayjs(data.enrollmentStartDate).format('YYYY-MM-DD'),
    enrollmentEndDate: dayjs(data.enrollmentEndDate).format('YYYY-MM-DD'),
    ...(contact ? { salesContact: contact } : {}),
  }
}

/**
 * Creates a new demo customer (customer record + enrollment config).
 * Endpoint: POST /api/v1/customers/demo
 */
export async function createDemoCustomer(
  data: DemoCustomerStep1Data
): Promise<CreateDemoCustomerResponse> {
  const response = await epsClient.post<CreateDemoCustomerResponse>(
    '/api/v1/customers/demo',
    serializeCreateRequest(data)
  )
  return response.data
}

/** A row returned by GET /api/v1/customers/demo. */
export interface DemoCompanyListItem {
  id: string
  name: string
  status: string
  employee_count: number
}

export interface DemoCompanyListResponse {
  data: DemoCompanyListItem[]
  meta: { page: number; limit: number; total: number; totalPages: number }
}

/**
 * Lists demo companies.
 * Endpoint: GET /api/v1/customers/demo
 *
 * `name` is a partial match on the display name WITHOUT the "ZZ - DEMO - "
 * prefix — the server prepends it, which is also what scopes the result set
 * to demo companies. `sortBy` accepts only 'name' or 'status'
 * (employee_count is enriched in-memory server-side and can't be sorted on).
 */
export async function listDemoCompanies(
  params: {
    page?: number
    limit?: number
    name?: string
    sortBy?: 'name' | 'status'
    sortOrder?: 'asc' | 'desc'
  },
  config?: Pick<AxiosRequestConfig, 'cancelToken'>
): Promise<DemoCompanyListResponse> {
  const { data } = await epsClient.get<DemoCompanyListResponse>(
    '/api/v1/customers/demo',
    { params, ...config }
  )
  return data
}

/**
 * Fetches a single demo company.
 * Endpoint: GET /api/v1/customers/demo/:id
 *
 * Returns the same serialized customer record as GET /customers/:id, but 404s
 * when the id isn't a demo company, so a real customer can't be reached
 * through the demo surface.
 */
export async function getDemoCompany(
  customerId: string
): Promise<CreateDemoCustomerResponse> {
  const { data } = await epsClient.get<CreateDemoCustomerResponse>(
    `/api/v1/customers/demo/${customerId}`
  )
  return data
}

/**
 * Updates the editable fields of a demo company.
 * Endpoint: PUT /api/v1/customers/demo/:id
 *
 * Only the display name and sales contact are mutable. The server re-applies
 * the "ZZ - DEMO - " prefix, upserts the SURECO contact in place without
 * touching Billing/HR contacts, and leaves payroll cycle and the enrollment
 * dates alone.
 *
 * NOTE: omitting `salesContact` preserves the existing contact. To clear it,
 * send the object with every field blank.
 */
export async function updateDemoCompany(
  customerId: string,
  body: { companyName: string; salesContact?: SalesContactFields }
): Promise<CreateDemoCustomerResponse> {
  const { data } = await epsClient.put<CreateDemoCustomerResponse>(
    `/api/v1/customers/demo/${customerId}`,
    body
  )
  return data
}

/**
 * Requests a short-lived presigned S3 PUT URL for a demo quote file.
 * Endpoint: POST /api/v1/customers/:id/demo/presigned-upload-url
 *
 * The same `fileName` must be passed to `populateDemoCustomer` so the
 * backend can reconstruct the S3 key (DEMO/<fileName>).
 */
export async function getDemoQuoteUploadUrl(
  customerId: string,
  fileName: string
): Promise<{ presignedUploadUrl: string }> {
  const { data } = await epsClient.post<{ presignedUploadUrl: string }>(
    `${demoBase(customerId)}/presigned-upload-url`,
    { fileName }
  )
  return data
}

/**
 * Uploads the file bytes directly to S3 using the presigned URL.
 *
 * IMPORTANT: uses raw `fetch`, NOT `epsClient`. epsClient injects an
 * Authorization header and a JSON Content-Type; S3 rejects presigned
 * requests that carry an Authorization header. The presigned URL is also
 * signed without a ContentType constraint, so the PUT must not set one.
 */
export async function putFileToPresignedUrl(
  presignedUrl: string,
  file: File
): Promise<void> {
  const res = await fetch(presignedUrl, { method: 'PUT', body: file })
  if (!res.ok) throw new Error(`S3 upload failed (${res.status})`)
}

/**
 * Triggers employee population from the previously uploaded quote file.
 * Endpoint: POST /api/v1/customers/:id/demo/populate
 *
 * The backend returns { population, logins } (DemoSetupResult). This function
 * extracts `population` and normalises it so callers always receive a flat
 * DemoPopulationResult — with `skipped` and `errors` defaulted to [] so a
 * partial or missing array never crashes the UI.
 *
 * Throws 409 when the company already has members (one-shot operation).
 * Throws 400 when the customer is not a test client or the file lacks a
 * "Shoppable" sheet.
 */
export async function populateDemoCustomer(
  customerId: string,
  body: {
    fileName: string
    payrollCycle?: string
    benefitEffectiveDate?: string
    /** Frozen plan pricing for the contribution run; omit for live pricing. */
    benchmarkSnapshotId?: number
  }
): Promise<DemoPopulationResult> {
  const { data } = await epsClient.post<DemoSetupResult>(
    `${demoBase(customerId)}/populate`,
    body
  )
  const pop = data?.population ?? ({} as Partial<DemoPopulationResult>)
  return {
    employeesCreated: pop.employeesCreated ?? 0,
    dependentsCreated: pop.dependentsCreated ?? 0,
    skipped: pop.skipped ?? [],
    errors: pop.errors ?? [],
    contributionRunJobId: pop.contributionRunJobId,
  }
}

/**
 * Returns the status of the demo contribution run for a demo customer, or
 * null when the company never had one.
 * Endpoint: GET /api/v1/customers/:id/demo/contribution-status
 */
export async function getDemoContributionStatus(
  clientPublicKey: string
): Promise<DemoContributionStatusResponse | null> {
  const { data } = await epsClient.get<DemoContributionStatusResponse | null>(
    `${demoBase(clientPublicKey)}/contribution-status`
  )
  return data
}

/**
 * Returns the total number of members belonging to a demo customer.
 *
 * Uses `POST /api/v1/members/search` with `limit: 1` so the backend returns
 * only the `total` count without transferring the full roster.
 *
 * IMPORTANT: `isTestAccount` is intentionally omitted. Demo employees are
 * created with `isTestUser: true`; the repository only applies that filter
 * when the field is explicitly present, so omitting it counts all members
 * (including test users) — which is exactly what we want here.
 */
export async function getDemoEmployeeCount(
  customerId: string
): Promise<number> {
  const { data } = await epsClientV1.post('/members/search', {
    filters: { clientPublicKey: customerId },
    pagination: { offset: 0, limit: 1 },
  })
  return normalizeMembersSearchResponse(data).total
}

/**
 * Lists every benchmark plan snapshot — the same source the contributions tab
 * uses for its "Benchmark Plans Source" dropdown. Callers filter out disabled
 * and deleted snapshots.
 * Endpoint: GET /api/v1/benchmark-plans
 */
export async function getBenchmarkSnapshots(): Promise<BenchmarkSnapshot[]> {
  const { data } = await epsClientV1.get<{ data: BenchmarkSnapshot[] }>(
    '/benchmark-plans'
  )
  return data?.data ?? []
}
